import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import FileUploadField from '../base/FileUploadField';
import { useGuideBuilderGuide } from '../../contexts/GuideBuilderContext';
import { useUpdateStep } from '../../hooks/useGuides';
import { AcceptedFileType, FileUploadTabType } from '../base/file-upload/utils';
import { FileMetadata } from '@/services/apis/files';
import { GuideStepType } from '@/services/apis/guides';
import StepEditorWrapper from './StepEditorWrapper';

const ImageStepEditorForm: React.FC = () => {
  const {
    guide: { id: guideId },
    state: { selectedStep },
  } = useGuideBuilderGuide();
  const { t } = useTranslation('admin');
  const updateStep = useUpdateStep(guideId || '');
  const [fileMetadata, setFileMetadata] = useState<FileMetadata | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [shouldAutoSave, setShouldAutoSave] = useState(false);

  // Initialize fileMetadata from step content
  useEffect(() => {
    const content = selectedStep?.content as any;
    if (content?.image && typeof content.image === 'object' && 'id' in content.image) {
      setFileMetadata(content.image as FileMetadata);
    } else {
      setFileMetadata(null);
    }
    setFile(null);
    setShouldAutoSave(false);
  }, [selectedStep]);

  const handleFileUploaded = (metadata: FileMetadata) => {
    setFileMetadata(metadata);
    setFile(null);
    // Trigger auto-save after file upload
    setShouldAutoSave(true);
  };

  const handleRemove = () => {
    setFileMetadata(null);
    setFile(null);
  };

  const handleSave = async () => {
    if (!selectedStep?.id || !fileMetadata) return;

    try {
      await updateStep.mutateAsync({
        stepId: selectedStep.id,
        data: {
          content: {
            image: fileMetadata,
          },
        },
      });
    } catch (error) {
      console.error('Error saving image step:', error);
    }
  };

  const handleAutoSaveComplete = useCallback(() => {
    setShouldAutoSave(false);
  }, []);

  if (!selectedStep) {
    return null;
  }

  return (
    <StepEditorWrapper
      title={t('guides.builder.stepEditor.imageStep') || 'Image Step'}
      stepType={GuideStepType.IMAGE}
      onSave={handleSave}
      isSaveDisabled={!fileMetadata || updateStep.isPending}
      isPending={updateStep.isPending}
      shouldAutoSave={shouldAutoSave}
      onAutoSaveComplete={handleAutoSaveComplete}
    >
      {fileMetadata ? (
        <div className="space-y-4">
          <div className="relative">
            <img
              src={fileMetadata.url}
              alt={fileMetadata.name}
              className="w-full h-auto max-h-96 object-contain rounded-lg border border-gray-200 dark:border-gray-700"
            />
            <Button
              isIconOnly
              color="danger"
              variant="solid"
              size="sm"
              className="absolute top-2 right-2"
              onPress={handleRemove}
              aria-label={t('common.remove') || 'Remove image'}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-sm text-foreground font-medium mb-1">{fileMetadata.name}</p>
            <p className="text-xs text-muted-foreground">
              {fileMetadata.type} •{' '}
              {fileMetadata.size > 0 ? `${(fileMetadata.size / 1024 / 1024).toFixed(2)} MB` : 'External URL'}
            </p>
          </div>
        </div>
      ) : (
        <FileUploadField
          file={file}
          onChange={setFile}
          acceptedTypes={[AcceptedFileType.IMAGE]}
          shownTabs={[FileUploadTabType.FILE_UPLOAD, FileUploadTabType.EXTERNAL_URL, FileUploadTabType.MEDIA_LIBRARY]}
          onFileUploaded={handleFileUploaded}
          maxSize={10}
        />
      )}
    </StepEditorWrapper>
  );
};

export default ImageStepEditorForm;
